import { useMutation, useQuery } from '@tanstack/react-query';
import {
  completeAssessment,
  getAssessmentByToken,
  saveAssessmentProgress,
  type SaveProgressBody,
  type ShareStatus,
  type TakePayload,
} from '../api/takeApi';
import { getAssessmentReport, type AssessmentReport } from '../api/reportApi';
import type { V2AssessmentReport } from '../api/v2ReportApi';
import type { V2TakePayload } from '../api/v2TakeApi';

/**
 * Load an assessment to take/resume by its link token (public).
 *
 * Resolves to whichever question set the assessment was stamped with — the
 * client opens one URL and the response says which. Narrow with `isV2Payload`
 * before reading `content`.
 */
export function useAssessmentByToken(token: string) {
  return useQuery<TakePayload | V2TakePayload>({
    queryKey: ['longevity-assessment', 'take', token],
    queryFn: () => getAssessmentByToken(token),
    enabled: !!token,
    retry: false,
  });
}

/** Save progress (answers, vision, concern). */
export function useSaveProgress(token: string) {
  return useMutation({
    mutationFn: (body: SaveProgressBody) => saveAssessmentProgress(token, body),
  });
}

/** Finish the assessment with the client's sharing choice. */
export function useCompleteAssessment(token: string) {
  return useMutation({
    mutationFn: (shareStatus: ShareStatus) => completeAssessment(token, shareStatus),
  });
}

/**
 * Fetch a completed assessment's full report (authed; gated server-side).
 * Resolves to whichever version the assessment was sent with — narrow with
 * `isV2Report` before rendering.
 */
export function useAssessmentReport(id: string) {
  return useQuery<AssessmentReport | V2AssessmentReport>({
    queryKey: ['longevity-assessment', 'report', id],
    queryFn: () => getAssessmentReport(id),
    enabled: !!id,
    retry: false,
  });
}

/**
 * Carry the assessment link token across the Kinde signup round-trip so the new
 * account can claim (re-own) the assessment on first sign-in (D2). The care-seeker
 * dashboard card (CareSeekerLongevityCard) reads it on mount, calls claim, and
 * clears it on success.
 */
const CLAIM_TOKEN_KEY = 'bquest_longevity_claim_token';

export function stashAssessmentClaimToken(token: string): void {
  try {
    localStorage.setItem(CLAIM_TOKEN_KEY, token);
  } catch {
    /* storage unavailable — token claim falls back to email-match on first sign-in */
  }
}

export function readAssessmentClaimToken(): string | null {
  try {
    return localStorage.getItem(CLAIM_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function clearAssessmentClaimToken(): void {
  try {
    localStorage.removeItem(CLAIM_TOKEN_KEY);
  } catch {
    /* no-op */
  }
}
